import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axiosInstance from '../AxiosInstance';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.dark,
  color: theme.palette.common.white,
  fontWeight: 'bold',
}));

const EnterFavoriteSales = () => {
  const [favorites, setFavorites] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const todayDate = new Date().toISOString().split('T')[0];

  useEffect(() => {
    const fetchFavoritesData = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get('/products/favorites');
        const favProducts = response.data;

        if (favProducts.length === 0) {
          setFavorites([]);
          setSalesData([]);
          setLoading(false);
          return;
        }

        const salesRows = await Promise.all(
          favProducts.map(async (product) => {
            let units = [];
            let defaultUnitId = null;
            let defaultUnitType = '';
            let suggestedPrice = '';

            try {
              const unitsRes = await axiosInstance.get(`/units/product/${product.product_id}`);
              units = unitsRes.data.filter((u) => u.unit_category === 'selling');
            } catch (err) {
              console.error('Error fetching units for product', product.product_id, err);
            }

            try {
              const topUnitRes = await axiosInstance.get(`/sales/top-selling-unit/${product.product_id}`);
              defaultUnitId = topUnitRes.data.unit_id;
              defaultUnitType = topUnitRes.data.unit_type;
            } catch (err) {
              if (units.length > 0) {
                defaultUnitId = units[0].unit_id;
                defaultUnitType = units[0].unit_type;
              }
            }

            if (defaultUnitId) {
              try {
                const priceRes = await axiosInstance.get(
                  `/sales/price-suggestions/${product.product_id}/${defaultUnitId}`
                );
                if (priceRes.data.has_price_history) {
                  suggestedPrice = priceRes.data.suggested_retail_price.toString();
                }
              } catch (err) {
                console.error('Error fetching price for product', product.product_id, err);
              }
            }

            return {
              product_id: product.product_id,
              productDisplay: formatProductDisplay(product),
              units,
              unit_id: defaultUnitId || '',
              unit_type: defaultUnitType,
              retail_price: suggestedPrice,
              quantity: '',
              discount: '0',
              sale_date: todayDate,
            };
          })
        );

        setFavorites(favProducts);
        setSalesData(salesRows);
      } catch (error) {
        console.error('Error fetching favorites:', error);
        setSnackbarMessage('Error loading favorite products.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };

    fetchFavoritesData();
  }, [todayDate]);

  const formatProductDisplay = (product) => {
    let display = product.product_name;
    if (product.brand) display += ` (${product.brand})`;
    if (product.variety || product.size) {
      display += ` - ${[product.variety, product.size].filter(Boolean).join(', ')}`;
    }
    return display;
  };

  const handleFieldChange = (index, field, value) => {
    setSalesData((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const handleUnitChange = async (index, unitId) => {
    const row = salesData[index];
    const selectedUnit = row.units.find((u) => u.unit_id === unitId);

    setSalesData((prev) =>
      prev.map((r, i) =>
        i === index
          ? { ...r, unit_id: unitId, unit_type: selectedUnit?.unit_type || '' }
          : r
      )
    );

    if (unitId) {
      try {
        const priceRes = await axiosInstance.get(
          `/sales/price-suggestions/${row.product_id}/${unitId}`
        );
        if (priceRes.data.has_price_history) {
          setSalesData((prev) =>
            prev.map((r, i) =>
              i === index
                ? { ...r, retail_price: priceRes.data.suggested_retail_price.toString() }
                : r
            )
          );
        }
      } catch (err) {
        console.error('Error fetching price for new unit:', err);
      }
    }
  };

  const handleSubmit = async () => {
    const salesToSubmit = salesData.filter(
      (row) =>
        row.quantity &&
        parseFloat(row.quantity) > 0 &&
        row.unit_id &&
        row.retail_price
    );

    if (salesToSubmit.length === 0) {
      setSnackbarMessage('Please enter quantity for at least one product.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        sales: salesToSubmit.map((row) => ({
          product_id: row.product_id,
          unit_id: row.unit_id,
          retail_price: parseFloat(row.retail_price),
          quantity: parseFloat(row.quantity),
          discount: parseFloat(row.discount || 0),
          sale_date: row.sale_date,
        })),
      };

      const response = await axiosInstance.post('/sales/batch', payload);
      setSnackbarMessage(
        `Sales added successfully! Transaction ID: ${response.data.transactionId}`
      );
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      setSalesData((prev) =>
        prev.map((row) => ({ ...row, quantity: '', discount: '0' }))
      );
    } catch (error) {
      console.error('Error submitting batch sales:', error);
      setSnackbarMessage(error.response?.data?.error || 'Error submitting sales.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  if (loading) {
    return (
      <Container sx={{ marginTop: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading favorite products...</Typography>
      </Container>
    );
  }

  if (favorites.length === 0) {
    return (
      <Container sx={{ marginTop: 4 }}>
        <Typography variant="h4" gutterBottom>
          Enter Sales of Favorite Products
        </Typography>
        <Alert severity="info">
          No favorite products found. Go to View Products and mark some products as
          favorites (star icon) to use this feature.
        </Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ marginTop: 4 }}>
      <Typography variant="h4" gutterBottom>
        Enter Sales of Favorite Products
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enter quantity sold for your favorite products. Leave quantity blank to skip a
        product.
      </Typography>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell align="center">Product Name</StyledTableCell>
              <StyledTableCell align="center">Unit Type</StyledTableCell>
              <StyledTableCell align="center">Retail Price (K)</StyledTableCell>
              <StyledTableCell align="center">Quantity Sold</StyledTableCell>
              <StyledTableCell align="center">Discount (K)</StyledTableCell>
              <StyledTableCell align="center">Sale Date</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {salesData.map((row, index) => (
              <TableRow key={row.product_id}>
                <TableCell sx={{ minWidth: 200 }}>{row.productDisplay}</TableCell>
                <TableCell sx={{ minWidth: 120 }}>
                  <FormControl fullWidth size="small">
                    <Select
                      value={row.unit_id}
                      onChange={(e) => handleUnitChange(index, e.target.value)}
                      displayEmpty
                    >
                      {row.units.length === 0 ? (
                        <MenuItem value="" disabled>
                          No units
                        </MenuItem>
                      ) : (
                        row.units.map((unit) => (
                          <MenuItem key={unit.unit_id} value={unit.unit_id}>
                            {unit.unit_type}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell sx={{ minWidth: 100 }}>
                  <TextField
                    type="number"
                    size="small"
                    value={row.retail_price}
                    onChange={(e) => handleFieldChange(index, 'retail_price', e.target.value)}
                    inputProps={{ min: 0, step: 0.01 }}
                    fullWidth
                  />
                </TableCell>
                <TableCell sx={{ minWidth: 100 }}>
                  <TextField
                    type="number"
                    size="small"
                    value={row.quantity}
                    onChange={(e) => handleFieldChange(index, 'quantity', e.target.value)}
                    inputProps={{ min: 0, step: 0.01 }}
                    fullWidth
                    placeholder="0"
                  />
                </TableCell>
                <TableCell sx={{ minWidth: 80 }}>
                  <TextField
                    type="number"
                    size="small"
                    value={row.discount}
                    onChange={(e) => handleFieldChange(index, 'discount', e.target.value)}
                    inputProps={{ min: 0, step: 0.01 }}
                    fullWidth
                  />
                </TableCell>
                <TableCell sx={{ minWidth: 140 }}>
                  <TextField
                    type="date"
                    size="small"
                    value={row.sale_date}
                    onChange={(e) => handleFieldChange(index, 'sale_date', e.target.value)}
                    fullWidth
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Submitting...' : 'Add Sale'}
        </Button>
      </Box>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EnterFavoriteSales;
